import { DataTable } from "@primereact/ui/datatable";
import {
  useState,
  type ChangeEvent,
  type FormEvent,
  type MouseEvent,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { CatalogItemResponse } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { Checkbox } from "@primereact/ui/checkbox";
import { InputText as Input } from "@primereact/ui/inputtext";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { Paginator } from "@primereact/ui/paginator";
import { Tabs } from "@primereact/ui/tabs";
import { apiDelete, apiGet, apiPatch, apiPost } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { InventoryTabs } from "./InventoryTabs.js";
import { useLocations } from "./stock-operations.js";
import { ItemEditor } from "./ItemEditor.js";
import { ErrorPanel, LoadingPanel, Section, formatMoney } from "../shared.js";

type CatalogTab = "items" | "stock" | "movements" | "locations";

export type PackageItemDraft = { itemId: string; quantity: string };

export type ItemDraft = {
  id?: string;
  sku: string;
  barcode: string;
  kind: "item" | "package";
  stockEnabled: boolean;
  defaultLocationId: string;
  defaultUnitCost: string;
  reorderPoint: string;
  name: string;
  description: string;
  unitPrice: string;
  taxRate: string;
  active: boolean;
  packageItems: PackageItemDraft[];
};

const EMPTY: ItemDraft = {
  sku: "",
  barcode: "",
  kind: "item",
  stockEnabled: true,
  defaultLocationId: "",
  defaultUnitCost: "",
  reorderPoint: "",
  name: "",
  description: "",
  unitPrice: "0",
  taxRate: "8.1",
  active: true,
  packageItems: [],
};

function CatalogPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const canManageCatalog = permissions.has("inventory.manage_catalog");
  const canViewInventory = permissions.has("inventory.view");
  const canManageLocations = permissions.has("inventory.manage_locations");
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get("tab");
  const tab: CatalogTab =
    requestedTab === "stock" ||
    requestedTab === "movements" ||
    requestedTab === "locations"
      ? requestedTab
      : "items";
  const setTab = (next: CatalogTab) =>
    setParams(next === "items" ? {} : { tab: next }, { replace: true });
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState<ItemDraft>({ ...EMPTY });
  const query = useQuery({
    queryKey: ["catalog", includeInactive, search, page],
    queryFn: () =>
      apiGet<{
        data: CatalogItemResponse[];
        page: number;
        pageSize: number;
        total: number;
      }>("/catalog/items", {
        query: {
          includeInactive,
          q: search || undefined,
          page,
          pageSize: 25,
          sortField: "name",
          sortOrder: 1,
        },
      }),
  });
  const itemOptions = useQuery({
    queryKey: ["catalog", "package-item-options"],
    queryFn: () =>
      apiGet<{ data: CatalogItemResponse[] }>("/catalog/items", {
        query: { kind: "item", active: true, pageSize: 100, sortField: "name" },
      }).then((response) => response.data),
  });
  const locations = useLocations();
  const save = useMutation({
    mutationFn: () => {
      const body = {
        sku: draft.sku || undefined,
        barcode: draft.barcode || undefined,
        kind: draft.kind,
        stockEnabled: draft.kind === "item" || draft.stockEnabled,
        defaultLocationId: draft.defaultLocationId || null,
        defaultUnitCost: draft.defaultUnitCost || undefined,
        reorderPoint: draft.reorderPoint || undefined,
        name: draft.name,
        description: draft.description || undefined,
        unitPrice: draft.unitPrice,
        taxRate: draft.taxRate,
        active: draft.active,
        packageItems: draft.kind === "package" ? draft.packageItems : [],
      };
      return draft.id
        ? apiPatch(`/catalog/items/${draft.id}`, body)
        : apiPost("/catalog/items", body);
    },
    onSuccess: async () => {
      setDraft({ ...EMPTY });
      setEditorOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["catalog"] });
    },
  });
  const removeItem = useMutation({
    mutationFn: (id: string) => apiDelete(`/catalog/items/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["catalog"] });
    },
  });
  const editItem = (item: CatalogItemResponse) => {
    setDraft({
      id: item.id,
      sku: item.sku ?? "",
      barcode: item.barcode ?? "",
      kind: item.kind,
      stockEnabled: item.stockEnabled,
      defaultLocationId: item.defaultLocationId ?? "",
      defaultUnitCost: item.defaultUnitCost ?? "",
      reorderPoint: item.reorderPoint ?? "",
      name: item.name,
      description: item.description ?? "",
      unitPrice: item.unitPrice,
      taxRate: item.taxRate,
      packageItems: item.packageItems.map((component) => ({
        itemId: component.itemId,
        quantity: component.quantity,
      })),
      active: item.active,
    });
    setEditorOpen(true);
  };
  const openNew = () => {
    setDraft({ ...EMPTY });
    setEditorOpen(true);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  return (
    <>
      <PageHeader
        title={t("catalog.title", { defaultValue: "Catalog & inventory" })}
        subtitle={t("catalog.subtitle", {
          defaultValue: "Manage items, packages, stock and locations.",
        })}
        actions={
          canManageCatalog ? (
            <Button onClick={openNew}>
              {t("catalog.add", { defaultValue: "Add item" })}
            </Button>
          ) : null
        }
      />
      <Tabs.Root
        value={tab}
        onValueChange={(event: { value: unknown }) =>
          setTab(String(event.value) as typeof tab)
        }
      >
        <Tabs.List className="mb-4 overflow-x-auto">
          <Tabs.Tab value="items">
            {t("catalog.items", { defaultValue: "Items" })}
          </Tabs.Tab>
          {canViewInventory ? (
            <>
              <Tabs.Tab value="stock">
                {t("inventory.stock", { defaultValue: "Stock" })}
              </Tabs.Tab>
              <Tabs.Tab value="movements">{t("inventory.movements")}</Tabs.Tab>
            </>
          ) : null}
          {canManageLocations ? (
            <Tabs.Tab value="locations">
              {t("inventory.locations", { defaultValue: "Locations" })}
            </Tabs.Tab>
          ) : null}
        </Tabs.List>
        <Tabs.Panel value="items">
          <Section
            title={t("catalog.title", { defaultValue: "Catalog" })}
            actions={
              <label className="flex items-center gap-2 text-sm">
                <Checkbox.Root
                  inputId="catalog-include-inactive"
                  checked={includeInactive}
                  onCheckedChange={(event: { checked: boolean }) => {
                    setIncludeInactive(Boolean(event.checked));
                    setPage(1);
                  }}
                >
                  <Checkbox.Box>
                    <Checkbox.Indicator match="checked">
                      <i className="pi pi-check" />
                    </Checkbox.Indicator>
                  </Checkbox.Box>
                </Checkbox.Root>
                {t("catalog.inactive", { defaultValue: "Show inactive" })}
              </label>
            }
          >
            {query.isLoading ? (
              <LoadingPanel />
            ) : query.error ? (
              <ErrorPanel error={query.error} />
            ) : (
              <>
                <DataTable.Root data={query.data?.data ?? []} dataKey="id">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-sm text-app-muted">
                      {query.data?.total ?? 0}{" "}
                      {t("catalog.items", { defaultValue: "items" })}
                    </span>
                    <Input
                      value={search}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setSearch(event.target.value);
                        setPage(1);
                      }}
                      placeholder={t("catalog.search", {
                        defaultValue: "Search catalog",
                      })}
                      aria-label={t("common.search", {
                        defaultValue: "Search catalog",
                      })}
                    />
                  </div>
                  <DataTable.TableContainer>
                    <DataTable.Table>
                      <DataTable.THead>
                        <DataTable.Row>
                          {[
                            [
                              "name",
                              t("catalog.name", { defaultValue: "Name" }),
                            ],
                            [
                              "kind",
                              t("catalog.kind", { defaultValue: "Kind" }),
                            ],
                            [
                              "unitPrice",
                              t("catalog.price", { defaultValue: "Price" }),
                            ],
                            [
                              "taxRate",
                              t("catalog.tax", { defaultValue: "VAT %" }),
                            ],
                            [
                              "stockEnabled",
                              t("inventory.stock", { defaultValue: "Stock" }),
                            ],
                            [
                              "active",
                              t("common.status", { defaultValue: "Status" }),
                            ],
                          ].map(([field, label]) => (
                            <DataTable.THeadCell key={field}>
                              <DataTable.Sort field={field}>
                                <DataTable.THeadTitle>
                                  {label}
                                </DataTable.THeadTitle>
                                <DataTable.SortIndicator />
                              </DataTable.Sort>
                            </DataTable.THeadCell>
                          ))}
                          <DataTable.THeadCell>
                            {t("common.actions")}
                          </DataTable.THeadCell>
                        </DataTable.Row>
                      </DataTable.THead>
                      <DataTable.TBody>
                        {(context: { item: unknown }) => {
                          const item = context.item as CatalogItemResponse;
                          const tracksStock =
                            item.kind === "item" || item.stockEnabled;
                          return (
                            <DataTable.Row
                              key={item.id}
                              className={
                                canManageCatalog ? "cursor-pointer" : undefined
                              }
                              onClick={() => canManageCatalog && editItem(item)}
                            >
                              <DataTable.Cell className="font-medium">
                                {item.name}
                                <span className="block text-xs text-app-muted">
                                  {item.sku || "—"}
                                </span>
                              </DataTable.Cell>
                              <DataTable.Cell className="capitalize">
                                {item.kind}
                                {item.kind === "package" ? (
                                  <span className="block text-xs text-app-muted">
                                    {item.packageItems.length}{" "}
                                    {t("catalog.components", {
                                      defaultValue: "components",
                                    })}
                                  </span>
                                ) : null}
                              </DataTable.Cell>
                              <DataTable.Cell>
                                {formatMoney(item.unitPrice)}
                              </DataTable.Cell>
                              <DataTable.Cell>{item.taxRate}%</DataTable.Cell>
                              <DataTable.Cell>
                                {tracksStock
                                  ? (item.inventory?.onHand ?? "0.000")
                                  : "—"}
                                {item.defaultLocation ? (
                                  <span className="block text-xs text-app-muted">
                                    {item.defaultLocation.code}
                                  </span>
                                ) : null}
                              </DataTable.Cell>
                              <DataTable.Cell>
                                {item.active
                                  ? t("common.active", {
                                      defaultValue: "Active",
                                    })
                                  : t("common.inactive", {
                                      defaultValue: "Inactive",
                                    })}
                              </DataTable.Cell>
                              <DataTable.Cell>
                                {canManageCatalog ||
                                (canViewInventory && tracksStock) ? (
                                  <div className="flex gap-1">
                                    {canViewInventory && tracksStock ? (
                                      <Button
                                        type="button"
                                        size="small"
                                        variant="text"
                                        icon="pi pi-box"
                                        aria-label={t("inventory.stock", {
                                          defaultValue: "Manage stock",
                                        })}
                                        onClick={(
                                          event: MouseEvent<HTMLButtonElement>,
                                        ) => {
                                          event.stopPropagation();
                                          setTab("stock");
                                        }}
                                      />
                                    ) : null}
                                    {canManageCatalog ? (
                                      <Button
                                        type="button"
                                        size="small"
                                        variant="text"
                                        icon="pi pi-pencil"
                                        aria-label={t("common.edit")}
                                        onClick={(
                                          event: MouseEvent<HTMLButtonElement>,
                                        ) => {
                                          event.stopPropagation();
                                          editItem(item);
                                        }}
                                      />
                                    ) : null}
                                    {canManageCatalog && item.active ? (
                                      <Button
                                        type="button"
                                        size="small"
                                        variant="text"
                                        severity="danger"
                                        icon="pi pi-trash"
                                        aria-label={t("common.delete")}
                                        loading={
                                          (removeItem.isPending &&
                                            removeItem.variables === item.id) ||
                                          undefined
                                        }
                                        onClick={(
                                          event: MouseEvent<HTMLButtonElement>,
                                        ) => {
                                          event.stopPropagation();
                                          removeItem.mutate(item.id);
                                        }}
                                      />
                                    ) : null}
                                  </div>
                                ) : null}
                              </DataTable.Cell>
                            </DataTable.Row>
                          );
                        }}
                      </DataTable.TBody>
                      <DataTable.EmptyTBody>
                        <DataTable.Row>
                          <DataTable.Cell
                            colSpan={7}
                            className="py-10 text-center text-app-muted"
                          >
                            {t("catalog.empty", {
                              defaultValue: "No catalog items.",
                            })}
                          </DataTable.Cell>
                        </DataTable.Row>
                      </DataTable.EmptyTBody>
                    </DataTable.Table>
                  </DataTable.TableContainer>
                </DataTable.Root>
                <Paginator.Root
                  className="mt-3"
                  page={page}
                  total={query.data?.total ?? 0}
                  itemsPerPage={25}
                  onPageChange={(event: { value: number }) =>
                    setPage(event.value)
                  }
                />
              </>
            )}
          </Section>
        </Tabs.Panel>
        {canViewInventory && tab !== "items" ? (
          <InventoryTabs tab={tab} />
        ) : null}
      </Tabs.Root>
      <ItemEditor
        draft={draft}
        setDraft={setDraft}
        editorOpen={editorOpen}
        setEditorOpen={setEditorOpen}
        submit={submit}
        saving={save.isPending}
        saveError={save.error}
        locations={locations.data}
        itemOptions={itemOptions.data}
      />
    </>
  );
}

export const catalogRoutes = [
  {
    path: "catalog",
    element: <CatalogPage />,
  },
];
